import { LocalizedError } from '@/lib/i18n/errors';
import { nativeTimestamp } from '@/lib/api/native-timestamp';
import type { Call, RawCallParticipant } from './call-view';

export interface CallOwner { tenant: string; user: string }
export type CallOperation = 'start' | 'answer' | 'reject' | 'end' | 'token' | 'history' | 'active' | 'history_page' | 'stats' | 'detail';
export const noCall = '00000000-0000-0000-0000-000000000000';
const uuid = (value: unknown): value is string => typeof value === 'string' && value !== noCall && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value);
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const integer = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) >= 0;
const optionalString = (value: unknown) => value === undefined || typeof value === 'string';
const write = (operation: CallOperation) => ['start', 'answer', 'reject', 'end'].includes(operation);

export class CallAPIError extends LocalizedError {
  constructor(key = 'read_failed', readonly uncertain = false, status?: number) {
    super('calls', 'api.' + key, {}, status);
  }
}
function fail(operation: CallOperation): never {
  throw new CallAPIError(write(operation) ? 'unconfirmed' : 'read_failed', write(operation));
}
function ownedMeta(value: unknown, owner: CallOwner, operation: CallOperation, callId?: string): Record<string, unknown> {
  if (!uuid(owner.tenant) || !uuid(owner.user) || !record(value) || value.success !== true || !record(value.meta)) fail(operation);
  const meta = value.meta;
  if (meta.tenant_id !== owner.tenant || meta.user_id !== owner.user || meta.operation !== operation ||
      callId !== undefined && meta.call_id !== callId || meta.call_id !== noCall && !uuid(meta.call_id)) fail(operation);
  return meta;
}

export type NativeCall = Call & { tenant_id: string; room_name: string; created_at: string };
function nativeCall(value: unknown, owner: CallOwner, operation: CallOperation, active: boolean): NativeCall {
  if (!record(value) || !uuid(value.id) || value.tenant_id !== owner.tenant || !uuid(value.initiated_by) || !uuid(value.callee_id) ||
      (value.type !== 'voice' && value.type !== 'video') || !['initiated', 'ringing', 'accepted', 'rejected', 'missed', 'ended'].includes(String(value.status)) ||
      typeof value.is_group_call !== 'boolean' || !integer(value.duration) || !nativeTimestamp(value.created_at) || value.room_name !== 'call_' + value.id ||
      !optionalString(value.caller_name) || !optionalString(value.callee_name) || !Array.isArray(value.participants) ||
      value.start_time !== undefined && !nativeTimestamp(value.start_time) || value.end_time !== undefined && !nativeTimestamp(value.end_time) ||
      value.status === 'accepted' && value.start_time === undefined || active && !['initiated', 'ringing', 'accepted'].includes(String(value.status))) fail(operation);
  const participants: RawCallParticipant[] = [], users = new Set<string>(), ids = new Set<string>();
  let me = false;
  for (const row of value.participants) {
    if (!record(row) || !uuid(row.id) || !uuid(row.user_id) || ids.has(row.id) || users.has(row.user_id) ||
        !optionalString(row.user_name) || row.role !== 'host' && row.role !== 'participant' ||
        !['connecting', 'connected', 'disconnected', 'rejected'].includes(String(row.status)) || typeof row.audio !== 'boolean' || typeof row.video !== 'boolean') fail(operation);
    ids.add(row.id); users.add(row.user_id);
    if (row.user_id === owner.user && (!active || row.status === 'connecting' || row.status === 'connected')) me = true;
    const participant: RawCallParticipant = { id: row.id, user_id: row.user_id, role: row.role, status: row.status as string, audio: row.audio, video: row.video };
    if (row.user_name !== undefined) participant.user_name = row.user_name as string;
    participants.push(participant);
  }
  if (value.is_group_call ? value.callee_id !== value.initiated_by || !me : value.initiated_by === value.callee_id || value.initiated_by !== owner.user && value.callee_id !== owner.user) fail(operation);
  const call: NativeCall = {
    id: value.id, tenant_id: owner.tenant, caller_id: value.initiated_by, callee_id: value.callee_id, type: value.type,
    status: value.status as string, is_group_call: value.is_group_call, room_name: value.room_name as string,
    created_at: value.created_at, duration_sec: value.duration, participants,
  };
  if (value.caller_name !== undefined) call.caller_name = value.caller_name as string;
  if (value.callee_name !== undefined) call.callee_name = value.callee_name as string;
  if (value.start_time !== undefined) call.started_at = value.start_time as string;
  if (value.end_time !== undefined) call.ended_at = value.end_time as string;
  return call;
}

export interface StartCallSnapshot { body: { type: 'voice' | 'video'; callee_id?: string; callee_ids?: string[] }; callees: string[]; group: boolean }
export function snapshotCallStart(value: unknown, owner: CallOwner): StartCallSnapshot {
  if (!uuid(owner.user) || !uuid(owner.tenant) || !record(value) || value.type !== 'voice' && value.type !== 'video' ||
      ('calleeId' in value) === ('calleeIds' in value) || Object.keys(value).some(key => !['type', 'calleeId', 'calleeIds'].includes(key))) throw new CallAPIError('bad_input');
  if ('calleeId' in value) {
    if (!uuid(value.calleeId)) throw new CallAPIError('bad_input');
    if (value.calleeId === owner.user) throw new CallAPIError('self');
    return { body: { type: value.type, callee_id: value.calleeId }, callees: [value.calleeId], group: false };
  }
  if (!Array.isArray(value.calleeIds) || !value.calleeIds.length || value.calleeIds.length > 100 || value.calleeIds.some(id => !uuid(id))) throw new CallAPIError('bad_input');
  const callees = [...new Set(value.calleeIds as string[])].filter(id => id !== owner.user);
  if (!callees.length) throw new CallAPIError('self');
  return { body: { type: value.type, callee_ids: [...value.calleeIds] as string[] }, callees, group: true };
}

/** Pure native DTO readers. Captured credentials/revision/cancellation and
 * uncertain-action recovery belong to the transport, not to these parsers. */
export function readCallState(value: unknown, owner: CallOwner, operation: 'active' | 'start' | 'answer', callId?: string, snapshot?: StartCallSnapshot): NativeCall | null {
  const meta = ownedMeta(value, owner, operation, callId);
  if (!record(value)) fail(operation);
  if (operation === 'active' && value.data === null) {
    if (meta.call_id !== noCall) fail(operation);
    return null;
  }
  const call = nativeCall(value.data, owner, operation, true);
  if (meta.call_id !== call.id || operation === 'answer' && call.status !== 'accepted') fail(operation);
  if (operation === 'answer' && (call.is_group_call ? call.participants?.find(row => row.user_id === owner.user)?.status !== 'connected' : call.callee_id !== owner.user)) fail(operation);
  if (operation === 'start') {
    if (!snapshot || call.caller_id !== owner.user || call.type !== snapshot.body.type || call.status !== 'ringing' || call.is_group_call !== snapshot.group ||
        !snapshot.group && call.callee_id !== snapshot.callees[0] || call.participants?.length !== snapshot.callees.length + 1) fail(operation);
    const required = new Set([owner.user, ...snapshot.callees]);
    for (const row of call.participants ?? []) {
      if (!row.user_id || !required.delete(row.user_id) || row.status !== 'connecting' || (row.role === 'host') !== (row.user_id === owner.user) || row.video !== (snapshot.body.type === 'video') || row.audio !== true) fail(operation);
    }
    if (required.size) fail(operation);
  }
  return call;
}
export function readCallHistory(value: unknown, owner: CallOwner, input: { limit: number; offset: number }): { calls: NativeCall[]; total: number } {
  const meta = ownedMeta(value, owner, 'history', noCall);
  if (!integer(input.limit) || input.limit < 1 || input.limit > 100 || !integer(input.offset) || meta.limit !== input.limit || meta.offset !== input.offset || !integer(meta.total) ||
      !record(value) || !Array.isArray(value.data) || value.data.length > input.limit || value.data.length > meta.total) fail('history');
  const calls = value.data.map(row => nativeCall(row, owner, 'history', false));
  if (new Set(calls.map(row => row.id)).size !== calls.length) fail('history');
  return { calls, total: meta.total };
}
export function readCallCommand(value: unknown, owner: CallOwner, operation: 'reject' | 'end', id: string): void {
  if (!uuid(id)) fail(operation);
  ownedMeta(value, owner, operation, id);
  if (!record(value) || typeof value.message !== 'string' || !value.message || 'data' in value) fail(operation);
  // A legacy nil service return, not a persisted-state or media-delivery proof.
}
export function readCallDetail(value:unknown,owner:CallOwner,id:string):NativeCall {
 if(!uuid(id))fail('detail');
 ownedMeta(value,owner,'detail',id);if(!record(value))fail('detail');
 const row=nativeCall(value.data,owner,'detail',false);if(row.id!==id)fail('detail');return row;
}
export function readCallToken(value: unknown, owner: CallOwner, id: string): { token: string; url: string; room_name: string } {
  if (!uuid(id)) fail('token');
  ownedMeta(value, owner, 'token', id);
  if (!record(value) || !record(value.data)) fail('token');
  const data = value.data;
  if (typeof data.token !== 'string' || !data.token.trim() || typeof data.url !== 'string' || data.room_name !== 'call_' + id) fail('token');
  let url: URL;
  try { url = new URL(data.url); } catch { fail('token'); }
  if (!url.hostname || url.username || url.password || url.hash || !['wss:', 'ws:', 'https:', 'http:'].includes(url.protocol)) fail('token');
  return { token: data.token, url: data.url, room_name: data.room_name as string };
}
export function callAPIProblem(value: unknown, status: number, operation: CallOperation): CallAPIError {
  const statuses: Record<string, number> = { unauthorized: 401, bad_input: 400, denied: 403, not_found: 404, recipient_unavailable: 404, busy: 409, ended: 409, self: 400, unavailable: 503, read_failed: 500, unconfirmed: 500 };
  if (record(value) && value.success === false && record(value.error) && value.error.status === status && typeof value.error.may_have_changed === 'boolean') {
    for (const [key, http] of Object.entries(statuses)) {
      if (http !== status || value.error.code !== 'calls_' + key) continue;
      const expected = key === 'unconfirmed' ? write(operation) : operation === 'start' && ['busy', 'ended', 'denied'].includes(key);
      const variable = operation === 'start' && key === 'unavailable';
      if (key === 'unconfirmed' && !write(operation) || !variable && value.error.may_have_changed !== expected) continue;
      return new CallAPIError(key, value.error.may_have_changed, status);
    }
  }
  return new CallAPIError(write(operation) ? 'unconfirmed' : 'read_failed', write(operation), status);
}
